import {
  Badge,
  type BadgeColor,
  GridCellText,
  Icon,
  SvgIcon,
  type TableColumn,
  Toggle,
  Tooltip,
} from '@civilgrid/ui/components';
import { cn, OPERATOR_TYPES } from '@civilgrid/ui/utils';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { updateDataRequest as apiUpdateRequest, getCivilSearchers } from '~/api/civilsearch';
import {
  type BillingTier,
  billingTierLabels,
  civilSearchTierLabels,
  outcomeLabels,
  requestStatusLabels,
  requestTypeMap,
  utilitiesPresentLabels,
} from '~/common/dataRequestUtils';
import { type DataRequestNote, type DataRequestV2, RequestStatus, type RequestType } from '~/common/types';
import HoverCard from '~/components2/HoverCard';
import { Notify } from '~/components2/Notify';
import { dateTimeSortingFn } from '~/components2/Table';
import { formatDate } from '../common';
import AssetType, { ASSET_TYPES } from '../common/AssetType';
import { US_STATES } from '../common/USStates';
import { isPastDue, mdy, NextFollowUpCell, nextFollowUpSortingFn } from './common';
import RequestTypeCell from './RequestTypeCell';

function formatNoteTimestamp(iso: string) {
  return DateTime.fromISO(iso).toFormat('M/d/yyyy h:mma');
}

function composeInternalNotesText(requestNotes: DataRequestNote[] | undefined, notesInternal: string | undefined) {
  const parts = (requestNotes ?? []).map((n) => n.note);
  if (notesInternal) parts.push(notesInternal);
  return parts.join('\n\n');
}

export const useColumns = (): TableColumn<DataRequestV2>[] => {
  // Fetch civil searchers to check tier assignments
  const { data: civilSearchers = [] } = useQuery({
    queryKey: ['civilSearchers'],
    queryFn: getCivilSearchers,
  });

  // Get query client for cache updates
  const queryClient = useQueryClient();

  // Create a map of user email to their assigned tiers
  const userTiersMap = useMemo(() => {
    const map: Record<string, string[]> = {};
    civilSearchers.forEach((searcher) => {
      map[searcher.email] = searcher.tiers;
    });
    return map;
  }, [civilSearchers]);

  const assignedToOptions = useMemo(() => {
    return civilSearchers.map((searcher) => ({
      value: searcher.userId,
      label: searcher.name || searcher.email,
    }));
  }, [civilSearchers]);

  const utilityPresenceOptions = useMemo(() => {
    return Object.entries(utilitiesPresentLabels).map(([key, label]) => ({
      value: key,
      label,
    }));
  }, []);

  const requestStatusOptions = useMemo(() => {
    return Object.entries(requestStatusLabels).map(([key, label]) => ({
      value: key,
      label,
    }));
  }, []);

  const outcomeOptions = useMemo(() => {
    return Object.entries(outcomeLabels).map(([key, label]) => ({
      value: key,
      label,
    }));
  }, []);

  const requestTypeOptions = useMemo(() => {
    return Object.entries(requestTypeMap).map(([key, label]) => ({
      value: key,
      label,
    }));
  }, []);

  const agencyTypeOptions = useMemo(() => {
    return ASSET_TYPES.map((type) => ({ value: type, label: type }));
  }, []);

  const civilSearchTierOptions = useMemo(() => {
    return Object.entries(civilSearchTierLabels).map(([key, label]) => ({
      value: key,
      label,
    }));
  }, []);

  const billingTierOptions = useMemo(() => {
    return Object.entries(billingTierLabels).map(([key, label]) => ({
      value: key,
      label,
    }));
  }, []);

  return [
    {
      id: 'isFlagged',
      size: 85,
      accessorKey: 'isFlagged',
      header: 'Flagged',
      cell: (cell) => {
        const id = cell.row.original.id;
        const v = cell.row.original.isFlagged;
        const { requestId } = cell.row.original;

        return (
          <div className="flex w-full items-center justify-center">
            <Toggle
              size="icon"
              variant="icon"
              className="group"
              aria-label={requestId ? `Flag request ${requestId}` : 'Flag request'}
              data-testid={`civilsearch-request-flag-${id}`}
              defaultPressed={v ?? false}
              pressed={v ?? false}
              onPressedChange={async (e) => {
                try {
                  const res = await apiUpdateRequest(id, { isFlagged: e });
                  if (!res.id) throw new Error('Bad response!');

                  const queries = queryClient.getQueriesData<{
                    requests: DataRequestV2[];
                    documentCount: number;
                    search: { [key: string]: any };
                  }>({
                    queryKey: ['data-requests-v2'],
                  });

                  queries.forEach(([key, data]) => {
                    if (data) {
                      queryClient.setQueryData(key, {
                        ...data,
                        requests: data.requests.map((r) => {
                          return r.id === id ? { ...r, isFlagged: e } : r;
                        }),
                      });
                    }
                  });
                } catch {
                  Notify.error({ message: 'An error occurred while flagging' });
                }
              }}
            >
              <SvgIcon icon="flag" className="fill-current group-data-[state=on]:hidden group-hover:hidden" />
              <SvgIcon
                icon="flagFilled"
                className="fill-current hidden group-hover:block group-data-[state=on]:block"
              />
            </Toggle>
          </div>
        );
      },
      meta: {
        filterOperatorTypeConfig: OPERATOR_TYPES.BOOLEAN,
        filterValueConfig: { type: 'boolean' },
      },
    },
    {
      id: 'requestId',
      header: 'Request ID',
      size: 125,
      accessorKey: 'requestId',
      cell: (cell) => {
        const id = cell.row.original.id;
        const value = cell.getValue<string>();
        const link = `/admin/civil-search/requests/${id}/general`;

        return (
          <GridCellText>
            <a href={link} target="_blank" rel="noopener noreferrer" className="text-primary-dark underline">
              {value}
            </a>
          </GridCellText>
        );
      },
      enableHiding: false,
    },
    {
      id: 'assignedTo.id',
      header: 'Assigned To',
      size: 150,
      accessorFn: (req) => req.assignedTo?.id || '',
      cell: (cell) => {
        let isOutsideOfTierAssignment = false;
        const req = cell.row.original;
        const agency = req.agency;
        const userTiers = req.assignedTo?.email ? userTiersMap[req.assignedTo.email] : [];
        if (req.assignedTo?.email) {
          if (!!userTiers && !userTiers.includes(agency.civilSearchTier)) {
            isOutsideOfTierAssignment = true;
          }
        }

        const fullName = req.assignedTo?.name || req.assignedTo?.email || '';

        const content = (
          <GridCellText>
            <span className={classNames('rounded-sm', !!isOutsideOfTierAssignment && 'bg-purple-300 text-purple-900')}>
              {fullName}
            </span>
          </GridCellText>
        );

        if (isOutsideOfTierAssignment) {
          return (
            <HoverCard trigger={content} side="right">
              <div className="text-xs">
                This request is outside of <span className="font-semibold">{fullName}'s</span> CivilSearch Tier
                assignment. {fullName} was either the requested agency's default CivilSearch assignee at the time of
                submission, or was manually assigned this request post-submission.
                <br />
                <br />
                {fullName}'s tier(s): {userTiers.join(', ')}
                <br />
                {agency.agencyName} tier: {agency.civilSearchTier}
              </div>
            </HoverCard>
          );
        }

        return content;
      },
      meta: {
        filterLabel: 'Assigned To',
        filterOperatorTypeConfig: OPERATOR_TYPES.EXACT,
        filterValueConfig: {
          type: 'select',
          options: assignedToOptions,
        },
      },
    },
    {
      id: 'project.projectId',
      header: 'Project ID',
      size: 125,
      accessorFn: (req) => req.project.projectId,
      cell: (cell) => {
        const id = cell.row.original.project.id;
        const value = cell.getValue<string>();
        const link = `${window.location.origin}/projects/${id}`;

        return (
          <GridCellText>
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-dark hover:underline flex flex-row items-center"
            >
              <span>{value}</span>
              <Icon icon="externalLink" />
            </a>
          </GridCellText>
        );
      },
    },
    {
      id: 'requestStatus',
      header: 'Status',
      size: 125,
      accessorFn: (req) => requestStatusLabels[req.requestStatus],
      cell: (cell) => {
        const status = cell.row.original.requestStatus;
        const label = requestStatusLabels[status] || 'INVALID STATUS';
        let color: BadgeColor = 'red';

        if (status === RequestStatus.Pending) {
          color = 'yellow';
        } else if (status === RequestStatus.Complete) {
          color = 'green';
        } else if (status === RequestStatus.ActionRequired) {
          color = 'purple';
        } else if (status === RequestStatus.Cancelled || status === RequestStatus.Dismissed) {
          color = 'grey';
        } else if (status.startsWith('inProgress')) {
          color = 'blue';
        }

        return (
          <div className="flex w-full items-center justify-start">
            <Badge color={color}>
              <span className="truncate">{label}</span>
            </Badge>
          </div>
        );
      },
      meta: {
        filterOperatorTypeConfig: OPERATOR_TYPES.EXACT,
        filterValueConfig: {
          type: 'select',
          options: requestStatusOptions,
        },
      },
    },
    {
      id: 'utilityPresence',
      header: 'Utility Presence',
      size: 100,
      accessorFn: (req) => (req.utilityPresence ? utilitiesPresentLabels[req.utilityPresence] : ''),
      cell: ({ getValue }) => <GridCellText>{getValue<string>()}</GridCellText>,
      meta: {
        filterOperatorTypeConfig: OPERATOR_TYPES.EXACT,
        filterValueConfig: {
          type: 'select',
          options: utilityPresenceOptions,
        },
      },
    },
    {
      id: 'outcome',
      header: 'Outcome',
      size: 100,
      accessorFn: (req) => (req.outcome ? outcomeLabels[req.outcome] : ''),
      cell: ({ getValue }) => <GridCellText>{getValue<string>()}</GridCellText>,
      meta: {
        filterOperatorTypeConfig: OPERATOR_TYPES.EXACT,
        filterValueConfig: {
          type: 'select',
          options: outcomeOptions,
        },
      },
    },
    {
      id: 'project.name',
      header: 'Project Name',
      size: 150,
      accessorKey: 'project.name',
      cell: ({ getValue }) => <GridCellText>{getValue<string>()}</GridCellText>,
    },
    {
      id: 'project.billingTier',
      header: 'Project Type',
      size: 120,
      accessorFn: (req) =>
        billingTierLabels[(req.project.billingTier as BillingTier) ?? ''] ?? req.project.billingTier ?? '',
      cell: ({ getValue }) => <GridCellText>{getValue<string>()}</GridCellText>,
      meta: {
        filterOperatorTypeConfig: OPERATOR_TYPES.EXACT,
        filterValueConfig: {
          type: 'select',
          options: billingTierOptions,
        },
      },
    },
    {
      id: 'agency.agencyName',
      header: 'Agency',
      accessorKey: 'agency.agencyName',
      cell: ({ getValue }) => <GridCellText>{getValue<string>()}</GridCellText>,
    },
    {
      id: 'agency.civilSearchTier',
      header: 'CivilSearch Tier',
      accessorFn: (req) => civilSearchTierLabels[req.agency.civilSearchTier] ?? '',
      cell: ({ getValue }) => <GridCellText>{getValue<string>()}</GridCellText>,
      meta: {
        filterOperatorTypeConfig: OPERATOR_TYPES.EXACT,
        filterValueConfig: {
          type: 'select',
          options: civilSearchTierOptions,
        },
      },
    },
    {
      id: 'agency.type',
      header: 'Asset',
      size: 125,
      accessorKey: 'agency.type',
      cell: ({ getValue }) => <AssetType type={getValue<string>()} />,
      meta: {
        filterOperatorTypeConfig: OPERATOR_TYPES.EXACT,
        filterValueConfig: {
          type: 'select',
          options: agencyTypeOptions,
        },
      },
    },
    {
      id: 'agency.state',
      header: 'State',
      accessorKey: 'agency.state',
      cell: ({ getValue }) => <GridCellText>{getValue<string>()}</GridCellText>,
      meta: {
        filterOperatorTypeConfig: OPERATOR_TYPES.EXACT,
        filterValueConfig: {
          type: 'select',
          options: US_STATES,
        },
      },
    },
    {
      id: 'dataRequest.type',
      header: 'Request Type',
      accessorFn: (req) => requestTypeMap[req.type],
      size: 150,
      cell: ({ getValue }) => <RequestTypeCell type={getValue<RequestType>()} />,
      meta: {
        filterOperatorTypeConfig: OPERATOR_TYPES.EXACT,
        filterValueConfig: {
          type: 'select',
          options: requestTypeOptions,
        },
      },
    },
    {
      id: 'organization.name',
      header: 'Organization',
      accessorFn: (req) => (req.project.organization as any)?.name || '',
      cell: ({ getValue }) => <GridCellText>{getValue<string>()}</GridCellText>,
    },
    {
      id: 'createdBy.email',
      header: 'Created By',
      accessorFn: (req) => {
        const u = req.createdBy;
        const val = `${u.name} (${u.email})` || '';
        return val;
      },
      cell: ({ getValue }) => <GridCellText>{getValue<string>()}</GridCellText>,
    },
    {
      id: 'projectManager.name',
      header: 'Project Manager',
      accessorFn: (req) => {
        const u = req.project.projectManager;
        const val = `${u.name} (${u.email})` || '';
        return val;
      },
      cell: ({ getValue }) => <GridCellText>{getValue<string>()}</GridCellText>,
    },
    {
      id: 'nextFollowUpAt',
      header: 'Next Follow-up At',
      accessorFn: (request) =>
        isPastDue(request)
          ? request.fourthContactedAt === null
            ? 'Followup'
            : 'Timed out'
          : mdy(request.nextFollowUpAt),
      sortingFn: (rowA, rowB) => nextFollowUpSortingFn(rowA, rowB),
      cell: (cell) => (
        <GridCellText>
          <NextFollowUpCell cell={cell} />
        </GridCellText>
      ),
      meta: {
        filterLabel: 'Next Follow-up At',
        filterOperatorTypeConfig: OPERATOR_TYPES.DATE,
        filterValueConfig: { type: 'date' },
      },
    },
    {
      id: 'firstContactedAt',
      header: 'First Contacted At',
      accessorFn: (request) => mdy(request.firstContactedAt),
      sortingFn: (a, b) => dateTimeSortingFn(a, b, 'firstContactedAt'),
      cell: ({ getValue }) => <GridCellText>{getValue<string>()}</GridCellText>,
      meta: {
        filterLabel: 'First Contacted At',
        filterOperatorTypeConfig: OPERATOR_TYPES.DATE,
        filterValueConfig: { type: 'date' },
      },
    },
    {
      id: 'secondContactedAt',
      header: 'Second Contacted At',
      accessorFn: (request) => mdy(request.secondContactedAt),
      sortingFn: (a, b) => dateTimeSortingFn(a, b, 'secondContactedAt'),
      cell: ({ getValue }) => <GridCellText>{getValue<string>()}</GridCellText>,
      meta: {
        filterLabel: 'Second Contacted At',
        filterOperatorTypeConfig: OPERATOR_TYPES.DATE,
        filterValueConfig: { type: 'date' },
      },
    },
    {
      id: 'thirdContactedAt',
      header: 'Third Contacted At',
      accessorFn: (request) => mdy(request.thirdContactedAt),
      sortingFn: (a, b) => dateTimeSortingFn(a, b, 'thirdContactedAt'),
      cell: ({ getValue }) => <GridCellText>{getValue<string>()}</GridCellText>,
      meta: {
        filterLabel: 'Third Contacted At',
        filterOperatorTypeConfig: OPERATOR_TYPES.DATE,
        filterValueConfig: { type: 'date' },
      },
    },
    {
      id: 'fourthContactedAt',
      header: 'Fourth Contacted At',
      accessorFn: (request) => mdy(request.fourthContactedAt),
      sortingFn: (a, b) => dateTimeSortingFn(a, b, 'fourthContactedAt'),
      cell: ({ getValue }) => <GridCellText>{getValue<string>()}</GridCellText>,
      meta: {
        filterLabel: 'Fourth Contacted At',
        filterOperatorTypeConfig: OPERATOR_TYPES.DATE,
        filterValueConfig: { type: 'date' },
      },
    },
    {
      id: 'calledDate',
      header: 'Called Date',
      accessorFn: (req) => req.calledDate?.toLocaleString() ?? '',
      sortingFn: (a, b) => dateTimeSortingFn(a, b, 'calledDate'),
      cell: ({ getValue }) => <GridCellText>{getValue<string>()}</GridCellText>,
      meta: {
        filterLabel: 'Called Date',
        filterOperatorTypeConfig: OPERATOR_TYPES.DATE,
        filterValueConfig: { type: 'date' },
      },
    },
    // TODO: Add resolution action steps to form
    {
      id: 'resolutionDate',
      header: 'Resolution Date',
      accessorFn: (request) => mdy(request.resolutionDate),
      sortingFn: (a, b) => dateTimeSortingFn(a, b, 'resolutionDate'),
      cell: ({ getValue }) => <GridCellText>{getValue<string>()}</GridCellText>,
      meta: {
        filterLabel: 'Resolution Date',
        filterOperatorTypeConfig: OPERATOR_TYPES.DATE,
        filterValueConfig: { type: 'date' },
      },
    },
    {
      id: 'notesInternal',
      header: 'Internal Notes',
      accessorFn: (row) => composeInternalNotesText(row.requestNotes, row.notesInternal),
      cell: ({ row, getValue }) => {
        const text = getValue<string>();
        const { requestNotes, notesInternal } = row.original;
        const hasAnything = (requestNotes && requestNotes.length > 0) || !!notesInternal;
        return (
          <div className="min-w-0 w-full">
            <Tooltip
              contentChildren={
                hasAnything ? (
                  <div className="text-xs text-white max-w-xs space-y-2">
                    {requestNotes?.map((note) => (
                      <div key={note.id}>
                        <div className="font-semibold">
                          {note.user?.name} — {formatNoteTimestamp(note.createdAt)}
                        </div>
                        <div className="whitespace-pre-wrap">{note.note}</div>
                      </div>
                    ))}
                    {notesInternal && (
                      <div>
                        <div className="font-semibold italic">Original Note</div>
                        <div className="whitespace-pre-wrap">{notesInternal}</div>
                      </div>
                    )}
                  </div>
                ) : null
              }
            >
              <GridCellText>{text}</GridCellText>
            </Tooltip>
          </div>
        );
      },
    },
    {
      id: 'notes',
      header: 'Public Notes',
      accessorKey: 'notes',
      cell: ({ getValue }) => (
        <div className="min-w-0 w-full">
          <Tooltip
            contentChildren={
              <span className="text-xs text-white whitespace-pre-wrap max-w-xs block">{getValue<string>()}</span>
            }
          >
            <GridCellText>{getValue<string>()}</GridCellText>
          </Tooltip>
        </div>
      ),
    },
    {
      id: 'invoiceId',
      header: 'Invoice ID',
      size: 100,
      accessorFn: (r) => {
        if (r.invoices.length > 0) {
          return r.invoices.map((i) => `${r.requestId}-${i.invoiceId || ''}`).join(', ');
        }

        return '';
      },
      cell: ({ getValue }) => <GridCellText>{getValue<string>()}</GridCellText>,
    },
    {
      id: 'agencyInvoiceId',
      header: 'Agency Invoice ID',
      size: 100,
      accessorFn: (r) => {
        if (r.invoices.length) {
          return r.invoices
            .map((i) => (i.agencyInvoiceId ? `${r.requestId}-${i.agencyInvoiceId}` : undefined))
            .filter((i) => i !== undefined)
            .join(', ');
        }

        return '';
      },
      cell: ({ getValue }) => <GridCellText>{getValue<string>()}</GridCellText>,
    },
    {
      id: 'updatedAt',
      header: 'Updated At',
      size: 100,
      accessorFn: (req) => formatDate(req.updatedAt),
      sortingFn: (a, b) => dateTimeSortingFn(a, b, 'updatedAt'),
      cell: ({ getValue }) => <GridCellText>{getValue<string>()}</GridCellText>,
      meta: {
        filterLabel: 'Updated At',
        filterOperatorTypeConfig: OPERATOR_TYPES.DATE,
        filterValueConfig: { type: 'date' },
      },
    },
    {
      id: 'createdAt',
      header: 'Created At',
      size: 100,
      accessorFn: (req) => formatDate(req.createdAt),
      sortingFn: (a, b) => dateTimeSortingFn(a, b, 'createdAt'),
      cell: ({ getValue }) => <GridCellText>{getValue<string>()}</GridCellText>,
      meta: {
        filterLabel: 'Created At',
        filterOperatorTypeConfig: OPERATOR_TYPES.DATE,
        filterValueConfig: { type: 'date' },
      },
    },
    // hidden columns (for searching only)
    {
      id: 'refId',
      header: 'Ref ID',
      accessorFn: (req) => {
        return `[[REF-CG-${req.project.projectId}-${req.requestId}]]`;
      },
      cell: ({ getValue }) => <GridCellText>{getValue<string>()}</GridCellText>,
    },
    {
      id: 'organization.isInternal',
      header: 'Internal Org',
      meta: {
        filterOperatorTypeConfig: OPERATOR_TYPES.BOOLEAN,
        filterValueConfig: { type: 'boolean' },
      },
      cell: ({ getValue }) => {
        const value = getValue<boolean>();
        const style = cn(
          'inline rounded-l-full rounded-r-full px-3 py-0.5 text-center text-2xs',
          value ? 'bg-green-200 text-green-900' : 'bg-red-300 text-red-900',
        );
        return <span className={style}>{value ? 'True' : 'False'}</span>;
      },
    },
  ];
};
